import React, { useState, useEffect } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { clientsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users, Building, Church, User, Briefcase, Home, Bell, BellOff } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { ContactsManager } from '../components/clients/ContactsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const clientTypeLabels = {
  diocese: { label: 'Diocèse', icon: Home, color: 'bg-indigo-100 text-indigo-800' },
  paroisse: { label: 'Paroisse', icon: Church, color: 'bg-blue-100 text-blue-800' },
  eglise: { label: 'Église', icon: Building, color: 'bg-emerald-100 text-emerald-800' },
  association_ig: { label: 'Asso. intérêt général', icon: User, color: 'bg-amber-100 text-amber-800' },
  entreprise: { label: 'Entreprise / Fédé. Pro', icon: Briefcase, color: 'bg-orange-100 text-orange-800' },
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_type: 'entreprise',
    company_name: '',
    siret: '',
    address: '',
    contact: {
      name: '',
      email: '',
      phone: ''
    },
    notes: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, search, typeFilter]);

  const loadClients = async () => {
    try {
      const res = await clientsApi.list();
      setClients(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des bénéficiaires');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = clients;
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchLower) ||
        c.contact?.email?.toLowerCase().includes(searchLower) ||
        c.contact?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.client_type === typeFilter);
    }
    
    setFilteredClients(filtered);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        name: client.name,
        client_type: client.client_type,
        company_name: client.company_name || '',
        siret: client.siret || '',
        address: client.address || '',
        contact: client.contact || { name: '', email: '', phone: '' },
        notes: client.notes || '',
        convention: client.convention || false
      });
    } else {
      setSelectedClient(null);
      setFormData({
        name: '',
        client_type: 'entreprise',
        company_name: '',
        siret: '',
        address: '',
        contact: { name: '', email: '', phone: '' },
        notes: '',
        convention: false
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await clientsApi.update(selectedClient.id, formData);
        toast.success('Client mis à jour');
      } else {
        await clientsApi.create(formData);
        toast.success('Client créé');
      }
      setDialogOpen(false);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleContactsChange = (contacts, updated) => {
    const patch = { contacts };
    if (updated?.is_primary) {
      const contact = { name: updated.name, email: updated.email, phone: updated.phone };
      patch.contact = contact;
      setFormData((prev) => ({ ...prev, contact }));
    }
    setSelectedClient((prev) => ({ ...prev, ...patch }));
    setClients((prev) => prev.map((c) => (c.id === selectedClient.id ? { ...c, ...patch } : c)));
  };

  const handleDelete = async () => {
    try {
      await clientsApi.delete(selectedClient.id);
      toast.success('Client supprimé');
      setDeleteDialogOpen(false);
      loadClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Layout>
      <PageHeader 
        title="Gestion des Bénéficiaires" 
        description="Gérez les organisateurs et bénéficiaires des mises à disposition"
        actions={
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
            data-testid="add-client-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouveau bénéficiaire
          </Button>
        }
      />

      <div className="p-8 space-y-6" data-testid="clients-content">
        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Rechercher par nom, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="client-search-input"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[200px]" data-testid="client-type-filter">
                  <SelectValue placeholder="Type de client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="diocese">Diocèse</SelectItem>
                  <SelectItem value="paroisse">Paroisse</SelectItem>
                  <SelectItem value="eglise">Église</SelectItem>
                  <SelectItem value="association_ig">Asso. intérêt général</SelectItem>
                  <SelectItem value="entreprise">Entreprise / Fédé. Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" style={{ width: 40, height: 40 }} />
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">
                {clients.length === 0 ? 'Aucun client' : 'Aucun résultat'}
              </p>
              {clients.length === 0 && (
                <>
                  <p className="text-slate-400 mb-4">Ajoutez votre premier client</p>
                  <Button onClick={() => handleOpenDialog()} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
                    <Plus className="mr-2 h-4 w-4" />
                    Créer un client
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Relances</TableHead>
                  <TableHead>Réservations</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => {
                  const typeInfo = clientTypeLabels[client.client_type];
                  const TypeIcon = typeInfo?.icon || Building;
                  
                  return (
                    <TableRow 
                      key={client.id} 
                      className="table-row-hover"
                      data-testid={`client-row-${client.id}`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span data-testid={`client-name-${client.id}`}>{client.name}</span>
                          {client.company_name && (
                            <span
                              className="text-xs text-slate-500 font-normal italic"
                              data-testid={`client-company-${client.id}`}
                            >
                              {client.company_name}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo?.color}>
                          <TypeIcon className="mr-1 h-3 w-3" />
                          {typeInfo?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{client.contact?.name}</span>
                          {(client.contacts?.length || 0) > 1 && (
                            <Badge variant="outline" className="text-[10px]" data-testid={`client-contacts-count-${client.id}`}>
                              +{client.contacts.length - 1} interlocuteur{client.contacts.length > 2 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{client.contact?.email}</TableCell>
                      <TableCell className="text-slate-600">{client.contact?.phone}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!client.reminders_disabled}
                            onCheckedChange={async (checked) => {
                              try {
                                await api.put(`/clients/${client.id}/reminders?disabled=${!checked}`);
                                // Update local state
                                setClients(clients.map(c => 
                                  c.id === client.id ? { ...c, reminders_disabled: !checked } : c
                                ));
                                toast.success(checked ? 'Relances activées' : 'Relances désactivées');
                              } catch (error) {
                                toast.error('Erreur lors de la mise à jour');
                              }
                            }}
                            data-testid={`toggle-reminders-${client.id}`}
                          />
                          {client.reminders_disabled ? (
                            <BellOff className="h-4 w-4 text-slate-400" />
                          ) : (
                            <Bell className="h-4 w-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{client.reservation_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedClient(client);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              {selectedClient ? 'Modifier le client' : 'Nouveau client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom / Prénom *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Jean Dupont"
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select 
                value={formData.client_type} 
                onValueChange={(value) => setFormData({ ...formData, client_type: value })}
              >
                <SelectTrigger data-testid="client-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="diocese">Diocèse</SelectItem>
                  <SelectItem value="paroisse">Paroisse</SelectItem>
                  <SelectItem value="eglise">Église (mouvements, associations)</SelectItem>
                  <SelectItem value="association_ig">Association d'intérêt général</SelectItem>
                  <SelectItem value="entreprise">Entreprise / Fédération Pro / Asso +50 sal.</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Convention checkbox */}
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="convention"
                checked={formData.convention || false}
                onChange={(e) => setFormData({ ...formData, convention: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="client-convention-checkbox"
              />
              <Label htmlFor="convention" className="text-sm font-medium text-blue-800 cursor-pointer">
                Convention avec le Diocèse (accès salles sans surcoût)
              </Label>
            </div>

            {/* Show company_name for all types */}
            <div className="space-y-2">
              <Label>Raison sociale</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder={formData.client_type === 'particulier' ? "Nom d'entreprise si applicable" : "Association Saint-Vincent de Paul"}
                data-testid="client-company-name-input"
              />
            </div>

            {/* SIRET field (optional for all beneficiary types) */}
            <div className="space-y-2">
              <Label>SIRET <span className="text-slate-400">(facultatif)</span></Label>
              <Input
                value={formData.siret}
                onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                placeholder="123 456 789 00012"
                maxLength={17}
                data-testid="client-siret-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="12 rue de l'Église, 09100 Pamiers"
                data-testid="client-address-input"
              />
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Contact principal</Label>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Nom du contact *</Label>
                  <Input
                    value={formData.contact.name}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      contact: { ...formData.contact, name: e.target.value }
                    })}
                    required
                    placeholder="Jean Dupont"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.contact.email}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contact: { ...formData.contact, email: e.target.value }
                      })}
                      required
                      placeholder="contact@organisation.fr"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone *</Label>
                    <Input
                      value={formData.contact.phone}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contact: { ...formData.contact, phone: e.target.value }
                      })}
                      required
                      placeholder="06 12 34 56 78"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {selectedClient ? (
              <ContactsManager client={selectedClient} onChange={handleContactsChange} />
            ) : (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="contacts-after-create-hint">
                Le contact principal sera ajouté automatiquement aux interlocuteurs. Vous pourrez ajouter d'autres interlocuteurs (responsable, validateur, payeur) après la création.
              </p>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes internes..."
                rows={2}
                data-testid="client-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-client-button">
                {selectedClient ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le client "{selectedClient?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
